import React, { useCallback, useEffect, useState } from 'react';
import { useLanguage } from '@/state/language/LanguageProvider';
import { toast } from 'react-hot-toast';
import { assessmentEngineService } from '@/services/assessmentEngineService';
import type { AeShareLink } from '@/types/assessmentEngine';
import {
  AE_BTN_PRIMARY,
  AE_CARD,
  AE_INPUT,
  AE_LABEL,
  AE_MUTED,
  AE_SECTION_TITLE,
  AE_TEXT_DANGER_LINK,
  AE_TEXT_LINK,
} from '@/components/assessment-engine/layout/aeTheme';

interface ReportSharePanelProps {
  campaignId: string;
}

/** Create and manage OTP-protected live report share links. */
export const ReportSharePanel: React.FC<ReportSharePanelProps> = ({ campaignId }) => {
  const { t } = useLanguage();
  const [links, setLinks] = useState<AeShareLink[]>([]);
  const [email, setEmail] = useState('');
  const [creating, setCreating] = useState(false);

  const load = useCallback(async () => {
    try {
      setLinks(await assessmentEngineService.listShareLinks(campaignId));
    } catch {
      toast.error(t('assessmentEngine.report.failedLoad'));
    }
  }, [campaignId, t]);

  useEffect(() => {
    load();
  }, [load]);

  const handleCreate = async () => {
    setCreating(true);
    try {
      const link = await assessmentEngineService.createShareLink(campaignId, {
        recipient_email: email.trim() || undefined,
        viewer_scope: { aggregates: true, own_response: true },
      });
      toast.success(t('assessmentEngine.share.createLink'));
      setEmail('');
      setLinks((prev) => [link, ...prev]);
    } catch {
      toast.error(t('assessmentEngine.report.failedLoad'));
    } finally {
      setCreating(false);
    }
  };

  const handleRevoke = async (shareId: string) => {
    try {
      await assessmentEngineService.revokeShareLink(campaignId, shareId);
      setLinks((prev) => prev.filter((l) => l.id !== shareId));
      toast.success(t('assessmentEngine.share.revoke'));
    } catch {
      toast.error(t('assessmentEngine.report.failedLoad'));
    }
  };

  const copyUrl = (url: string) => {
    const full = `${window.location.origin}${url}`;
    void navigator.clipboard.writeText(full);
    toast.success(t('assessmentEngine.share.copy'));
  };

  return (
    <div className={`${AE_CARD} space-y-4`}>
      <h2 className={AE_SECTION_TITLE}>{t('assessmentEngine.share.title')}</h2>
      <p className={AE_MUTED}>{t('assessmentEngine.share.description')}</p>
      <div className="flex flex-wrap gap-2 items-end">
        <div>
          <label className={`${AE_LABEL} text-xs`}>
            {t('assessmentEngine.share.recipientEmail')}
          </label>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className={`${AE_INPUT} text-sm w-64`}
            placeholder="user@example.com"
          />
        </div>
        <button
          type="button"
          disabled={creating}
          onClick={handleCreate}
          className={AE_BTN_PRIMARY}
        >
          {t('assessmentEngine.share.createLink')}
        </button>
      </div>
      {links.length > 0 && (
        <ul className="divide-y divide-[var(--color-border)] text-sm">
          {links.map((link) => (
            <li key={link.id} className="py-3 flex flex-wrap items-center justify-between gap-2">
              <div>
                <p className="font-mono text-xs break-all text-[var(--color-surface-text)]">
                  {link.live_url}
                </p>
                {link.recipient_email && (
                  <p className={`${AE_MUTED} text-xs`}>{link.recipient_email}</p>
                )}
              </div>
              <div className="flex gap-3">
                <button type="button" onClick={() => copyUrl(link.live_url)} className={AE_TEXT_LINK}>
                  {t('assessmentEngine.share.copy')}
                </button>
                <button
                  type="button"
                  onClick={() => handleRevoke(link.id)}
                  className={AE_TEXT_DANGER_LINK}
                >
                  {t('assessmentEngine.share.revoke')}
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
